import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import BadgeAvatar from '../components/BadgeAvatar';
import ForumLoginModal from '../components/ForumLoginModal';

const Test = ({ user, logout }) => {
  const { difficulty } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(540); // 90 seconds per section * 6
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [isForumLoginOpen, setIsForumLoginOpen] = useState(false);

  const sections = [
    { key: 'diagnosis', label: 'Diagnosis' },
    { key: 'treatment_principle', label: 'Treatment Principle' },
    { key: 'differential', label: 'Differential Diagnosis' },
    { key: 'points', label: 'Acupuncture Points' },
    { key: 'nutrition', label: 'Nutrition Recommendations' },
    { key: 'daily_activities', label: 'Daily Life Advice' }
  ];

  useEffect(() => {
    fetchTest();
  }, [difficulty]);

  useEffect(() => {
    if (testStarted && !testCompleted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [testStarted, testCompleted, timeLeft]);

  const fetchTest = async () => {
    try {
      const response = await api.get(`/tests/featured/${difficulty}`);
      setTest(response.data);
    } catch (error) {
      console.error('Error fetching test:', error);
      toast.error('Failed to load test');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    if (!user) {
      setIsForumLoginOpen(true);
      return;
    }
    setTestStarted(true);
    setStartTime(Date.now());
  };

  const handleAnswerSelect = (sectionKey, answerIndex) => {
    setAnswers({ ...answers, [sectionKey]: answerIndex });
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmitTest = async () => {
    if (testCompleted) return;
    
    setTestCompleted(true);
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    try {
      const response = await api.post('/tests/submit', {
        test_id: test.id,
        answers: answers,
        time_taken: timeTaken
      });
      setResults(response.data);
      toast.success(`Test completed! Score: ${response.data.score}%`);
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error('Failed to submit test');
    }
  };

  const currentQuestion = test?.questions?.[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600'></div>
          <p className='mt-4 text-teal-600 font-medium'>Loading test...</p>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
        <div className='text-center'>
          <p className='text-gray-600 mb-4'>No test available</p>
          <Link to='/dashboard'>
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                </svg>
              </div>
              <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className='flex items-center space-x-4'>
              {user && (
                <>
                  <Link to={`/profile/${user.id}`}>
                    <BadgeAvatar 
                      commentCount={user.comment_count || 0}
                      size="md"
                      userName={user.name}
                      showLevel={false}
                    />
                  </Link>
                  <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>Logout</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className='container mx-auto px-4 py-8 max-w-4xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          {!testStarted ? (
            <div className='text-center space-y-6'>
              <Badge className={`${difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-lg px-6 py-2`}>
                {difficulty === 'simple' ? '📘 Simple Test' : '📕 Complex Test'}
              </Badge>
              <h1 className='text-3xl font-bold text-gray-900 mb-2' style={{ fontFamily: 'Playfair Display, serif' }}>TCM Competency Test</h1>
              
              {!user ? (
                <div className='max-w-2xl mx-auto'>
                  <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 shadow-lg'>
                    <div className='w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6'>
                      <svg className='w-10 h-10 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                      </svg>
                    </div>
                    <h2 className='text-2xl font-bold text-gray-900 mb-4'>Sign Up to Take Tests</h2>
                    <p className='text-gray-700 mb-6 text-lg'>
                      Create a free forum account to take competency tests, track your progress, and engage with the TCM community!
                    </p>
                    <div className='space-y-4 text-left mb-6'>
                      <div className='flex items-start space-x-3'>
                        <svg className='w-6 h-6 text-teal-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <p className='text-gray-700'>Take timed competency tests</p>
                      </div>
                      <div className='flex items-start space-x-3'>
                        <svg className='w-6 h-6 text-teal-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <p className='text-gray-700'>Track your test scores and progress</p>
                      </div>
                      <div className='flex items-start space-x-3'>
                        <svg className='w-6 h-6 text-teal-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <p className='text-gray-700'>Participate in case study discussions</p>
                      </div>
                      <div className='flex items-start space-x-3'>
                        <svg className='w-6 h-6 text-teal-600 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <p className='text-gray-700'>Earn badges for community contributions</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setIsForumLoginOpen(true)}
                      className='bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-lg px-8 py-6 rounded-full shadow-lg'
                    >
                      Create Forum Account
                    </Button>
                    <p className='text-sm text-gray-600 mt-4'>
                      Already have an account? <button onClick={() => setIsForumLoginOpen(true)} className='text-teal-600 hover:text-teal-700 font-semibold'>Log in here</button>
                    </p>
                  </div>
                  <Link to='/dashboard' className='inline-block mt-6'>
                    <Button variant='outline' className='rounded-full px-6'>
                      ← Back to Dashboard
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
              <p className='text-gray-600 mb-6'>Read the case study carefully before starting the test</p>
              
              <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 text-left border-2 border-teal-200 shadow-lg'>
                <div className='flex items-center space-x-3 mb-4'>
                  <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                    <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                    </svg>
                  </div>
                  <h3 className='text-xl font-bold text-gray-900'>Patient Case Study</h3>
                </div>
                <div className='bg-white rounded-xl p-6 border border-teal-100'>
                  <p className='text-gray-800 leading-relaxed text-lg whitespace-pre-line'>{test.case_scenario}</p>
                </div>
              </div>
              
              <div className='bg-amber-50 border-2 border-amber-300 rounded-xl p-6'>
                <div className='flex items-start space-x-3'>
                  <svg className='w-6 h-6 text-amber-600 flex-shrink-0 mt-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  <div className='text-left'>
                    <p className='text-sm font-bold text-amber-900 mb-2'>
                      ⏱ Timer Information
                    </p>
                    <p className='text-sm text-amber-800'>
                      <strong>Total Time:</strong> {Math.floor(test.time_limit / 60)} minutes ({test.time_limit} seconds) for all 6 questions
                    </p>
                    <p className='text-sm text-amber-800 mt-1'>
                      <strong>Average:</strong> 1 min 30 sec per question
                    </p>
                    <p className='text-sm text-amber-800 mt-1'>
                      <strong>Sections:</strong> Diagnosis, Treatment Principle, Differential Diagnosis, Acupuncture Points, Nutrition, Daily Life Advice
                    </p>
                    <p className='text-sm font-bold text-amber-900 mt-3'>
                      ⚠️ Timer starts when you click "Begin Questions" below
                    </p>
                  </div>
                </div>
              </div>
              
              <Button onClick={handleStartTest} className='bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-full px-12 py-6 text-lg shadow-lg hover:shadow-xl transition-all'>
                🚀 Begin Questions
              </Button>
              </>
              )}
            </div>
          ) : testCompleted ? (
            <div className='space-y-6'>
              <div className='text-center'>
                <div className='inline-block p-8 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-full mb-4'>
                  <svg className='w-20 h-20 text-teal-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                </div>
                <h2 className='text-3xl font-bold text-gray-900 mb-4' style={{ fontFamily: 'Playfair Display, serif' }}>Test Complete!</h2>
                <div className='bg-gray-50 rounded-xl p-8'>
                  <div className='text-5xl font-bold text-teal-600 mb-2'>{results?.score}%</div>
                  <p className='text-gray-600'>You answered {results?.correct} out of {results?.total} questions correctly</p>
                </div>
              </div>

              {/* Answer Review Section */}
              <div className='mt-8'>
                <h3 className='text-2xl font-bold text-gray-900 mb-6 text-center' style={{ fontFamily: 'Playfair Display, serif' }}>Answer Review</h3>
                <div className='space-y-4'>
                  {sections.map((section, sectionIndex) => {
                    const question = test?.questions?.[sectionIndex];
                    const userAnswer = answers[section.key];
                    const correctAnswer = results?.correct_answers?.[section.key];
                    const isCorrect = userAnswer === correctAnswer;

                    return (
                      <div key={section.key} className={`border-2 rounded-xl p-6 ${isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                        <div className='flex items-start justify-between mb-3'>
                          <h4 className='font-bold text-lg text-gray-900'>{section.label}</h4>
                          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                          </div>
                        </div>
                        
                        <p className='text-gray-700 mb-4 font-medium'>{question?.question}</p>
                        
                        <div className='space-y-2'>
                          {question?.options.map((option, optionIndex) => {
                            const isUserAnswer = userAnswer === optionIndex;
                            const isCorrectAnswer = correctAnswer === optionIndex;
                            
                            return (
                              <div 
                                key={optionIndex}
                                className={`p-3 rounded-lg border-2 ${
                                  isCorrectAnswer 
                                    ? 'border-green-500 bg-green-100' 
                                    : isUserAnswer 
                                      ? 'border-red-500 bg-red-100'
                                      : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className='flex items-center justify-between'>
                                  <span className='text-gray-900'>{option}</span>
                                  <div className='flex items-center space-x-2'>
                                    {isCorrectAnswer && (
                                      <span className='text-green-700 font-semibold text-sm'>✓ Correct Answer</span>
                                    )}
                                    {isUserAnswer && !isCorrectAnswer && (
                                      <span className='text-red-700 font-semibold text-sm'>Your Answer</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className='flex gap-4 justify-center mt-8'>
                <Link to='/student'>
                  <Button className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'>View All Scores</Button>
                </Link>
                <Link to='/dashboard'>
                  <Button variant='outline' className='rounded-full px-8'>Back to Dashboard</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className='space-y-6'>
              {/* Timer */}
              <div className='flex items-center justify-between mb-4'>
                <Badge className={`${difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0`}>
                  {difficulty === 'simple' ? '📘 Simple' : '📕 Complex'}
                </Badge>
                <div className='text-2xl font-bold text-teal-600'>
                  ⏱ {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
              </div>

              {/* Progress Bar */}
              <div className='w-full bg-gray-200 rounded-full h-3'>
                <div className='bg-gradient-to-r from-teal-500 to-cyan-600 h-3 rounded-full transition-all' style={{ width: `${progress}%` }}></div>
              </div>

              {/* Section Title */}
              <div className='text-center'>
                <h2 className='text-2xl font-bold text-gray-900 mb-2' style={{ fontFamily: 'Playfair Display, serif' }}>
                  {sections[currentSection].label}
                </h2>
                <p className='text-sm text-gray-600'>Question {currentSection + 1} of {sections.length}</p>
              </div>

              {/* Question */}
              <div className='bg-gray-50 rounded-xl p-6'>
                <p className='text-lg text-gray-900 mb-6'>{currentQuestion?.question}</p>
                <div className='space-y-3'>
                  {currentQuestion?.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(sections[currentSection].key, index)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        answers[sections[currentSection].key] === index
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-teal-300 bg-white'
                      }`}
                    >
                      <div className='flex items-center'>
                        <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                          answers[sections[currentSection].key] === index
                            ? 'border-teal-500 bg-teal-500'
                            : 'border-gray-300'
                        }`}>
                          {answers[sections[currentSection].key] === index && (
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
                            </svg>
                          )}
                        </div>
                        <span className='text-gray-900'>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className='flex justify-between pt-6 border-t border-gray-200'>
                <Button
                  onClick={handlePrevious}
                  disabled={currentSection === 0}
                  variant='outline'
                  className='rounded-full px-8'
                >
                  Previous
                </Button>
                {currentSection === sections.length - 1 ? (
                  <Button
                    onClick={handleSubmitTest}
                    disabled={Object.keys(answers).length !== sections.length}
                    className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'
                  >
                    Submit Test
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Forum Login Modal */}
      <ForumLoginModal
        isOpen={isForumLoginOpen}
        onClose={() => setIsForumLoginOpen(false)}
        onSuccess={(userData) => {
          window.location.reload(); // Reload to update user state
        }}
      />
    </div>
  );
};

export default Test;